import { useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';
import type { CourseModule } from '../../types/course';
import '../figma/figma.css';
import 'swiper/css';

interface CourseCarouselProps {
  modules: CourseModule[];
  onStart: (module: CourseModule) => void;
  onToggleLock: (module: CourseModule) => void;
  onGoHome: () => void;
}

const LockIcon = ({ locked }: { locked: boolean }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    {locked ? (
      <path
        d="M17 9H7V7C7 4.79086 8.79086 3 11 3C13.2091 3 15 4.79086 15 7V9H17C18.1046 9 19 9.89543 19 11V19C19 20.1046 18.1046 21 17 21H7C5.89543 21 5 20.1046 5 19V11C5 9.89543 5.89543 9 7 9H17ZM11 15V17H13V15H11Z"
        fill="currentColor"
      />
    ) : (
      <path
        d="M17 9H7C5.89543 9 5 9.89543 5 11V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V11C19 9.89543 18.1046 9 17 9ZM11 15H13V17H11V15ZM9 9V7C9 4.79086 10.7909 3 13 3C15.2091 3 17 4.79086 17 7H15C15 5.89543 14.1046 5 13 5C11.8954 5 11 5.89543 11 7V9H9Z"
        fill="currentColor"
      />
    )}
  </svg>
);

const CourseCarousel: React.FC<CourseCarouselProps> = ({ modules, onStart, onToggleLock, onGoHome }) => {
  const swiperRef = useRef<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const orderedModules = useMemo(() => modules.slice().sort((a, b) => a.position - b.position), [modules]);

  useEffect(() => {
    setActiveIndex(0);
  }, [modules.length]);

  const handleSlideChange = async () => {
    const swiper = swiperRef.current;
    if (!swiper) {
      return;
    }
    setActiveIndex(swiper.activeIndex ?? 0);
  };

  const goToSlide = async (index: number) => {
    const swiper = swiperRef.current;
    if (!swiper) {
      return;
    }
    swiper.slideTo(index);
    setActiveIndex(index);
  };

  if (orderedModules.length === 0) {
    return (
      <div className="course-carousel" role="status">
        <div className="course-carousel__placeholder">Aucun module disponible pour le moment.</div>
      </div>
    );
  }

  const activeModule = orderedModules[Math.min(activeIndex, orderedModules.length - 1)];

  return (
    <div className="course-carousel">
      <Swiper
        onSwiper={(instance) => {
          swiperRef.current = instance;
        }}
        slidesPerView={1}
        spaceBetween={16}
        speed={400}
        className="course-carousel__slides"
        onSlideChange={handleSlideChange}
      >
        {orderedModules.map((module) => (
          <SwiperSlide key={module.id} className="course-carousel__slide">
            <article className={`course-card${module.isLocked ? ' course-card--locked' : ''}`} style={{ ['--course-color' as string]: module.color }}>
              <button
                type="button"
                className="course-card__lock"
                onClick={() => onToggleLock(module)}
                aria-label={module.isLocked ? 'Déverrouiller le module' : 'Verrouiller le module'}
              >
                <LockIcon locked={module.isLocked} />
              </button>
              <div className="course-card__rails" aria-hidden="true">
                <span className="course-card__rail course-card__rail--left" style={{ ['--course-color' as string]: module.color }} />
                <span className="course-card__rail course-card__rail--right" style={{ ['--course-color' as string]: module.color }} />
              </div>
              <div className="course-card__surface">
                <h3 className="course-card__title">{module.title}</h3>
                {module.description ? <p className="course-card__description">{module.description}</p> : null}
              </div>
              {module.isLocked ? <div className="course-card__badge">Module verrouillé</div> : null}
            </article>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="course-carousel__dots" role="tablist" aria-label="Sélection du module">
        {orderedModules.map((module, index) => (
          <button
            key={module.id}
            type="button"
            className={`course-carousel__dot${activeIndex === index ? ' course-carousel__dot--active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Aller au module ${module.title}`}
            aria-selected={activeIndex === index}
          />
        ))}
      </div>

      {activeModule ? (
        <button
          type="button"
          className="course-carousel__cta"
          onClick={() => onStart(activeModule)}
          disabled={activeModule.isLocked}
        >
          <span className="course-carousel__cta-icon" aria-hidden="true">▶</span>
          {activeModule.isLocked ? 'Verrouillé' : 'Commencer'}
        </button>
      ) : null}

      <button type="button" className="course-carousel__home" onClick={onGoHome}>
        <span className="course-carousel__home-icon" aria-hidden="true">🏠</span>
        Home
      </button>
    </div>
  );
};

export default CourseCarousel;
